import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/react'
import { isNull } from 'lodash'
import type { PropsWithChildren, ReactElement } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IKReactWindowedSelect } from '../../../../../ui-kit'
import { TypeBadge } from '../../../../../utils/mediaLibrary'
import BulkEditModeDropdown from './BulkEditModeDropdown'
import type { CMFieldProps } from '.'

const CMMultiSelectField = ({
  cmField,
  disabled,
  readOnly,
  placeholder,
  isUpload,
  form,
  children,
  labelElement,
  showBulkEditFieldModeDropdown,
  bulkEditFieldMode,
  style,
  required,
  menuPosition,
  setBulkEditFieldMode,
  onInputChange,
}: PropsWithChildren<CMFieldProps>): ReactElement => {
  const { t } = useTranslation()

  const shouldShowFormField = useMemo(() => {
    if (!showBulkEditFieldModeDropdown) {
      return true
    }

    return bulkEditFieldMode?.[cmField.name] === 'set'
  }, [bulkEditFieldMode, cmField.name, showBulkEditFieldModeDropdown])

  const formattedValue = useMemo(() => {
    const isDisplayDisabled = readOnly || disabled

    if (
      !Array.isArray(form.values.upload[cmField.name]) ||
      form.values.upload[cmField.name].length === 0
    ) {
      return null
    }

    return form.values.upload[cmField.name].map(item => ({
      label: isDisplayDisabled ? (
        String(item)
      ) : (
        <>
          {String(item)} <TypeBadge val={item} />
        </>
      ),
      value: item,
    }))
  }, [form.values.upload[cmField.name], disabled, readOnly, cmField.name, cmField])

  const formattedPlaceholder = useMemo(() => {
    if (
      Array.isArray(cmField.schema.defaultValue) &&
      cmField.schema.defaultValue.length > 0 &&
      isUpload
    ) {
      return `${cmField.schema.defaultValue
        .map(val => `${val}[${typeof val}]`)
        .join(', ')} (Default value)`
    }

    return ''
  }, [cmField.schema.defaultValue, isUpload, cmField])

  const onChange = useCallback(
    (newVal: Array<{ label: string; value: string }>) => {
      form.setTouched({ ...form.touched, [cmField.name]: true })
      if (isNull(newVal)) {
        form.setFieldValue(`upload.['${cmField.name}']`, null)
      } else {
        form.setFieldValue(
          `upload.['${cmField.name}']`,
          newVal.map(item => item.value),
        )
      }
      onInputChange?.(cmField, form, newVal ? newVal.map(item => item.value) : undefined)
    },
    [form, cmField.name, onInputChange],
  )

  const options = useMemo(
    () =>
      cmField.schema.selectOptions?.map(item => ({
        label: String(item),
        value: item,
      })),
    [cmField.schema.selectOptions],
  )

  return (
    <FormControl
      marginBottom={isUpload ? 0 : '30px'}
      isInvalid={Boolean(
        form.errors.upload &&
          form.touched.upload &&
          form.errors.upload[cmField.name] &&
          form.touched.upload[cmField.name],
      )}
      isRequired={cmField.schema.isValueRequired}
      isDisabled={disabled}
      style={style}
    >
      {labelElement ?? <FormLabel>{cmField.label}</FormLabel>}
      <Box width="100%">
        {showBulkEditFieldModeDropdown &&
          bulkEditFieldMode &&
          setBulkEditFieldMode && (
            <BulkEditModeDropdown
              bulkEditFieldMode={bulkEditFieldMode}
              setBulkEditFieldMode={setBulkEditFieldMode}
              cmField={cmField}
              disabled={readOnly || disabled}
              onInputChange={() => {
                onInputChange?.(cmField, form)
              }}
            />
          )}
        {shouldShowFormField && (
          <>
            <IKReactWindowedSelect
              placeholder={
                formattedPlaceholder || placeholder || t('set_a_default_value', 'Set a default value')
              }
              variant="compact"
              captureMenuScroll={false}
              menuPlacement="auto"
              menuPosition={menuPosition}
              isClearable
              isMulti
              isDisabled={readOnly || disabled}
              value={formattedValue}
              options={options}
              onChange={onChange}
              required={required}
            />
            <FormErrorMessage>
              {form.errors.upload && form.errors.upload[cmField.name]}
            </FormErrorMessage>
          </>
        )}
      </Box>
      {children}
    </FormControl>
  )
}

export default CMMultiSelectField
